import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from "@/components/ui/alert-dialog";

interface AIAnalysisTabProps {
  className?: string;
}

type AIEngine = "openai" | "deepseek";

const AIAnalysisTab: React.FC<AIAnalysisTabProps> = ({
  className
}) => {
  const [analysisResult, setAnalysisResult] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [aiEngine, setAiEngine] = useState<AIEngine>("openai");
  const [showNetworkAlert, setShowNetworkAlert] = useState(false);

  const performAIAnalysis = async () => {
    setIsLoading(true);
    setAnalysisResult("");
    try {
      const {
        data,
        error
      } = await supabase.from("leaderboard").select("*");
      if (error) {
        throw new Error(`Error fetching leaderboard data: ${error.message}`);
      }
      const prompt = `
You are an expert sales director analyzing sales performance data. Based on the following sales data (including data ro region, country, account name, sales person name, product, industry, target, achievement, revenue gap, COGS, forecast, historical forecasts, historical actual revenues, sales stage pipeline, late stage pipeline, late stage to gap ratio, and historical win ratio), provide a comprehensive analysis and actionable recommendations. It is important to mention the account name, and then mention the account manager name.

DATA:
${JSON.stringify(data, null, 2)}

Provide an in-depth detailed analysis, and please structure your detailed response, highlighting the specific regions, countries, accounts, account managers, industries or product lines, using the following format:
1. Analysis on What's Working by region, country, account name, name:
   - Top Accounts and Performers: Identify regions, countries, accounts, account managers, industries or product lines exceeding their targets.
   - Strong Conversion Stages: Examine which pipeline stages contribute the most to closed deals.
   - Historical Trends: Compare historical win ratios to determine which regions, countries, accounts, account managers, industries or product lines are consistently successful.

2. Analysis on What's Not Working by region, country, account name, name:
   - Revenue Gap Drivers: Identify regions, countries, account managers, industries or product lines with the biggest shortfalls.
   - Pipeline Weaknesses: Analyze the late-stage-to-gap ratio to determine if deals are stalling.
   - Forecast Accuracy: Compare forecasted revenue to actual achievements to find forecasting discrepancies.

3. Areas for Improvement by region, country, account name, name:
   - Conversion Bottlenecks: Identify which pipeline stages (prospecting, qualification, proposal, negotiation) are losing the most deals.
   - Industry & Product Strategy: Reallocate resources from underperforming segments to high-potential areas.
   - Sales Execution: Identify whether poor performance is due to lack of prospecting, weak proposals, or ineffective negotiations.

4. Building a Revenue Bridge Plan by region, country, account name, name:
   - Increase Prospecting Volume: If pipeline input is too low, mandate higher lead generation activity.
   - Improve Deal Progression: Optimize conversion rates at weak pipeline stages through training or better incentives.
   - Close Late-Stage Gaps: Target stalled negotiations with executive involvement or discount strategies.
   - Optimize Forecasting: Adjust projections based on historical accuracy to create a realistic revenue target.
   - Comprehensive Revenue Management: a) Pricing Optimization: Develop a dynamic pricing strategy that reflects market demand and customer value perception. Regularly adjust prices to remain competitive and maximize revenue. b) Inventory Control: Implement strategies like overbooking in high-demand scenarios to ensure optimal utilization of inventory, thereby increasing sales. c) Targeted Promotions: Design promotions based on customer responsiveness to balance volume growth with profitability. Effective promotions can attract long-term commitments, enhancing revenue over time. d) Channel Management: Leverage various distribution channels strategically, considering the price sensitivity and cost associated with each, to broaden market reach and boost sales.
   - Customer-Centric Approaches: a) Enhance Customer Experience: Invest in training sales teams to provide exceptional service, fostering customer loyalty and repeat business. b) Loyalty Programs: Implement or revamp rewards programs to incentivize repeat purchases and strengthen customer relationships.
   - Digital Transformation: a) E-commerce Expansion: Broaden online product offerings to cater to a wider audience and adapt to changing purchasing behaviors. b) Data Analytics: Utilize analytics to gain insights into customer preferences and sales trends, enabling data-driven decision-making.
   - Market Adaptation: a) Stay Informed: Keep abreast of industry trends and economic indicators to anticipate market shifts and adjust strategies accordingly. b) Flexibility: Be prepared to pivot strategies in response to changing market conditions to maintain a competitive edge.

5. Conducting a Gross Profit Margin Analysis by region, country, account name, name:
   - Revenue: a) Implement a Pricing Strategy on contribution margin-based pricing to maximize profit by setting prices that cover variable costs and contribute positively to fixed costs. b) Utilize Revenue Management techniques to optimize pricing and inventory control, ensuring that prices reflect market demand and customer value perception. 
   - Cost of Goods Sold (COGS): a) Apply Cost Accounting methods to identify and control variable and fixed costs, thereby reducing COGS and improving gross profit margins. 
   - Gross Profit: a) Focus on increasing the Contribution Margin by enhancing the difference between sales revenue and variable costs, which directly impacts gross profit. 
   - Price Per Unit: a) Use Dynamic Pricing to adjust prices based on market conditions and demand elasticity to maximize revenue without sacrificing volume. 
   - Discount Rate: a) Use data-driven Strategic Discounting approaches to determine optimal discount rates that boost sales volume while maintaining profitability. 
   - Cost Per Unit: a) Increase production efficiency to achieve Economies of Scale to lower the cost per unit, thereby enhancing the contribution margin. 
   - Order Volume: a) Implement Volume-Based Pricing strategies that encourage larger orders, reducing per-unit costs and increasing total gross profit. 
`;
      const { data: aiData, error: aiError } = await supabase.functions.invoke('ai-chat-completion', {
        body: {
          messages: [{
            role: "system",
            content: "You are an expert global sales director with extensive regional and country sales experience, who analyzes sales performance data and makes recommendations."
          }, {
            role: "user",
            content: prompt
          }],
          model: 'gpt-4o-mini'
        }
      });

      if (aiError) {
        console.error("AI API error:", aiError);
        setShowNetworkAlert(true);
        return;
      }

      const content = aiData?.choices?.[0]?.message?.content;
      if (!content || content.trim() === "") {
        setShowNetworkAlert(true);
        return;
      }

      setAnalysisResult(content);
    } catch (error) {
      console.error("Error during analysis:", error);
      setShowNetworkAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const formatAnalysisText = (text: string) => {
    if (!text) return null;
    const lines = text.split('\n');
    const result = [];
    let inList = false;
    const subHeaders = ["Top Accounts and Performers", "Strong Conversion Stages", "Historical Trends", "Revenue Gap Drivers", "Pipeline Weaknesses", "Forecast Accuracy", "Conversion Bottlenecks", "Industry & Product Strategy", "Sales Execution", "Increase Prospecting Volume", "Improve Deal Progression", "Close Late-Stage Gaps", "Optimize Forecasting"];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const nextLine = i < lines.length - 1 ? lines[i + 1] : "";
      if (line.startsWith("## ") && /^\#\# \d+\.\s+/.test(line)) {
        if (i > 0) {
          result.push(<div key={`${i}-spacer-before`} className="h-4"></div>);
          result.push(<div key={`${i}-separator-before`} className="my-3">
            <Separator className="bg-primary-light/30" />
          </div>);
        }
        result.push(<div key={i} className="text-orange-500 text-lg font-bold">
            {line.substring(3)}
          </div>);
        result.push(<div key={`${i}-spacer`} className="h-2"></div>);
        inList = false;
        continue;
      }
      if (line.startsWith("Conclusion") || line === "Conclusion:") {
        result.push(<div key={`${i}-spacer-before-conclusion`} className="h-4"></div>);
        result.push(<div key={`${i}-separator-before-conclusion`} className="my-3">
          <Separator className="bg-primary-light/30" />
        </div>);
        result.push(<div key={i} className="text-orange-500 text-lg font-bold">
            {line}
          </div>);
        result.push(<div key={`${i}-spacer-after-conclusion`} className="h-2"></div>);
        inList = false;
        continue;
      }
      if (line.startsWith("- **") && line.endsWith("**:")) {
        result.push(<div key={`${i}-spacer-before-green`} className="h-2"></div>);
        const extractedText = line.substring(4, line.length - 3);
        result.push(<div key={i} className="font-bold text-green-500">
            {extractedText}
          </div>);
        result.push(<div key={`${i}-spacer-after-green`} className="h-2"></div>);
        continue;
      }
      if (/^\d+\.\s+/.test(line)) {
        if (i > 0) {
          result.push(<div key={`${i}-spacer-before`} className="h-4"></div>);
          result.push(<div key={`${i}-separator-before`} className="my-3">
            <Separator className="bg-primary-light/30" />
          </div>);
        }
        result.push(<div key={i}>{line}</div>);
        result.push(<div key={`${i}-spacer`} className="h-2"></div>);
        inList = false;
        continue;
      }
      const isSubHeader = subHeaders.some(header => line.includes(header));
      if (line.startsWith("### ")) {
        result.push(<div key={`${i}-spacer-before-sub`} className="h-2"></div>);
        result.push(<div key={i} className="text-blue-500 text-[14px] font-semibold underline">
          {line.substring(4)}
        </div>);
        inList = false;
        continue;
      }
      if (line.startsWith("#### ")) {
        result.push(<div key={`${i}-spacer-before-sub`} className="h-2"></div>);
        result.push(<div key={i} className="text-orange text-[12px] font-medium mb-2">
          {line.substring(5)}
        </div>);
        inList = false;
        continue;
      }
      if (isSubHeader && !line.startsWith("### ") && !line.startsWith("#### ")) {
        result.push(<div key={`${i}-spacer-before-cat`} className="h-2"></div>);
        result.push(<div key={i} className="text-blue-500 underline">{line}</div>);
        continue;
      }
      if (line.startsWith("---")) {
        result.push(<div key={`${i}-spacer-before-sep`} className="h-2"></div>);
        result.push(<div key={i} className="border-t border-primary-light my-2"></div>);
        result.push(<div key={`${i}-spacer-after-sep`} className="h-2"></div>);
        inList = false;
        continue;
      }
      if (line.startsWith("- ") && line.endsWith(":")) {
        result.push(<div key={`${i}-spacer-before-orange`} className="h-2"></div>);
        result.push(<div key={i} className="text-[#FEC6A1]">
          {line}
        </div>);
        continue;
      }
      if (line.startsWith("- ") || line.startsWith("-\t")) {
        if (line.includes("**")) {
          let segments = [];
          let lastIndex = 0;
          const regex = /\*\*(.*?)\*\*/g;
          let match;
          while ((match = regex.exec(line)) !== null) {
            if (match.index > lastIndex) {
              segments.push(<span key={`${i}-${lastIndex}`}>
                {line.substring(lastIndex, match.index)}
              </span>);
            }
            segments.push(<span key={`${i}-${match.index}`} className="text-[#D3E4FD]">
              {match[1]}
            </span>);
            lastIndex = match.index + match[0].length;
          }
          if (lastIndex < line.length) {
            segments.push(<span key={`${i}-${lastIndex}`}>
              {line.substring(lastIndex)}
            </span>);
          }
          result.push(<div key={i}>{segments}</div>);
        } else {
          result.push(<div key={i}>{line}</div>);
        }
        inList = true;
        if (inList && nextLine && !nextLine.startsWith("- ") && !nextLine.startsWith("-\t") && nextLine.trim() !== "") {
          result.push(<div key={`${i}-list-end`} className="h-2"></div>);
          inList = false;
        }
        continue;
      }
      if (line.includes("**")) {
        let segments = [];
        let lastIndex = 0;
        const regex = /\*\*(.*?)\*\*/g;
        let match;
        while ((match = regex.exec(line)) !== null) {
          if (match.index > lastIndex) {
            segments.push(<span key={`${i}-${lastIndex}`}>
              {line.substring(lastIndex, match.index)}
            </span>);
          }
          segments.push(<span key={`${i}-${match.index}`} className="text-[#D3E4FD]">
            {match[1]}
          </span>);
          lastIndex = match.index + match[0].length;
        }
        if (lastIndex < line.length) {
          segments.push(<span key={`${i}-${lastIndex}`}>
            {line.substring(lastIndex)}
          </span>);
        }
        result.push(<div key={i}>{segments}</div>);
        continue;
      }
      result.push(<div key={i}>{line}</div>);
    }
    return result;
  };

  const handleDownload = () => {
    const element = document.createElement("a");
    const file = new Blob([analysisResult], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `AI-Analysis-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return <div className={`text-white space-y-6 ${className || ''}`}>
      <div className="mb-4">
        <h2 className="font-bold mb-2 text-base">AI Sales Performance Analysis</h2>
        <p className="text-primary-light tex-[11px] text-xs my-[9px]">
          Click the button below to generate an AI-powered analysis of the current sales performance data. 
          The analysis will identify trends, issues, and provide actionable recommendations.
        </p>
        <div className="flex items-center gap-4">
          <Button onClick={performAIAnalysis} disabled={isLoading} className="bg-primary text-black hover:bg-primary/90 transition-colors">
            {isLoading ? <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Analyzing Sales Data...
              </> : "Start AI Sales Performance Analysis"}
          </Button>
          {analysisResult && (
            <Button 
              onClick={handleDownload} 
              className="bg-primary text-black hover:bg-primary/90 transition-colors"
            >
              Download Analysis
            </Button>
          )}
          <div className="flex items-center gap-2">
            <Label htmlFor="ai-engine" className="text-white whitespace-nowrap">Choose AI Engine:</Label>
            <RadioGroup id="ai-engine" value={aiEngine} onValueChange={value => setAiEngine(value as AIEngine)} className="flex gap-4">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="openai" id="openai" />
                <Label htmlFor="openai" className="text-primary-light">OpenAI</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="deepseek" id="deepseek" />
                <Label htmlFor="deepseek" className="text-primary-light">DeepSeek</Label>
              </div>
            </RadioGroup>
          </div>
        </div>
      </div>
      
      <div className="mt-6 h-[450px]">
        <h3 className="font-semibold mb-2">AI Analysis Results:</h3>
        <ScrollArea className="h-[410px] border-slate-700 text-primary-light p-4 text-[11px] overflow-auto border bg-secondary-DEFAULT rounded">
          {analysisResult ? formatAnalysisText(analysisResult) : <p className="text-primary-light/50 italic">Click "Start AI Sales Performance Analysis" to generate results.</p>}
        </ScrollArea>
        <p className="text-[12px] text-white">(Note: AI hallucination may occur. Validation of generated information is essential.)</p>
      </div>

      <AlertDialog open={showNetworkAlert} onOpenChange={setShowNetworkAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Network Issue</AlertDialogTitle>
            <AlertDialogDescription>
              Network conditions are affecting the query. Please try again, or try using a different AI engine.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowNetworkAlert(false)}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>;
};
export default AIAnalysisTab;
